import { useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { getIso2FromDialCode } from "@/lib/country-flags";
import { Capacitor } from "@capacitor/core";

const getCountryDisplayName = (countryName: string | null, dialCode: string | null): string => {
  const rawName = (countryName || '').trim();
  const rawDialCode = (dialCode || '').trim();
  const isNumericName = /^\+?\d+$/.test(rawName);
  if (rawName && !isNumericName) return rawName;

  const iso2 = getIso2FromDialCode(rawDialCode || rawName);
  if (!iso2) return rawName || rawDialCode;

  try {
    return new Intl.DisplayNames(['en'], { type: 'region' }).of(iso2) || rawName || rawDialCode;
  } catch {
    return rawName || rawDialCode;
  }
};

export interface CallRecord {
  id: string;
  destination_number: string;
  destination_country: string;
  destination_country_code: string;
  destination_type: string;
  duration_seconds: number | null;
  rate_per_minute_cents: number;
  cost_cents: number | null;
  status: string;
  started_at: string;
  connected_at: string | null;
  ended_at: string | null;
  call_quality_score: number | null;
  is_video: boolean;
  /** For elefa-to-elefa calls, the other party's user_id */
  elefa_user_id?: string;
}

export type ClearHistoryRange = 'all' | '7days' | '30days';

export interface PaginatedCallHistory {
  calls: CallRecord[];
  totalCount: number;
  hasMore: boolean;
}

export function useCallHistory(page = 0, pageSize = 20) {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const userId = user?.id;
  
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["call-history", userId, page, pageSize],
    queryFn: async (): Promise<PaginatedCallHistory> => {
      if (!userId) return { calls: [], totalCount: 0, hasMore: false };

      // Fetch PSTN call history (6-month retention window)
      const sixMonthsAgo = new Date();
      sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);
      const sixMonthsIso = sixMonthsAgo.toISOString();

      // Run both queries in PARALLEL - previously sequential, which is the
      // primary cause of the 2-4s "shows first call then hangs" symptom on
      // the Recents tab.
      // We also fetch one extra row instead of running an `exact` COUNT(*)
      // (which on Supabase requires a full table scan and was adding 1-3s of
      // latency on top of the data fetch). hasMore is derived from row count.
      const fetchSize = pageSize + 1;

      const [pstnResult, elefaResult, hiddenResult] = await Promise.all([
        supabase
          .from('call_history')
          .select('*')
          .eq('user_id', userId)
          .is('deleted_at', null)
          .gte('started_at', sixMonthsIso)
          .order('started_at', { ascending: false })
          .range(page * pageSize, page * pageSize + fetchSize - 1),
        supabase
          .from('elefa_calls')
          .select('*, caller:profiles!elefa_calls_caller_id_fkey(full_name, phone_number), callee:profiles!elefa_calls_callee_id_fkey(full_name, phone_number)')
          .or(`caller_id.eq.${userId},callee_id.eq.${userId}`)
          .is('deleted_at', null)
          .gte('started_at', sixMonthsIso)
          .order('started_at', { ascending: false })
          .limit(fetchSize),
        // Per-user hidden elefa call IDs (industry-standard per-side dismissal
        // for shared rows). Filtered client-side after fetch.
        (supabase.from('elefa_call_hidden' as never) as any)
          .select('call_id')
          .eq('user_id', userId),
      ]);

      const { data: pstnData, error: pstnError } = pstnResult;
      const { data: elefaCallsRaw } = elefaResult;
      const hiddenIds = new Set<string>(
        ((hiddenResult as { data?: Array<{ call_id: string }> })?.data ?? []).map((h) => h.call_id),
      );
      const elefaCalls = (elefaCallsRaw || []).filter((c: { id: string }) => !hiddenIds.has(c.id));


      if (pstnError) {
        console.error('Call history fetch error:', pstnError);
        throw pstnError;
      }

      // Map PSTN calls to include is_video
      type RawPstnCall = Record<string, unknown> & {
        is_video_call?: boolean | null;
        destination_country?: string | null;
        destination_country_code?: string | null;
      };
      const pstnCalls: CallRecord[] = (pstnData || []).map((c: RawPstnCall) => ({
        ...(c as object),
        destination_country: getCountryDisplayName(c.destination_country ?? '', c.destination_country_code ?? ''),
        is_video: c.is_video_call || false,
      })) as CallRecord[];

      // Convert elefa calls to CallRecord format
      type RawElefaCall = {
        id: string;
        caller_id: string;
        callee_id: string;
        callee?: { full_name?: string | null; phone_number?: string | null } | null;
        caller?: { full_name?: string | null; phone_number?: string | null } | null;
        duration_seconds?: number | null;
        started_at?: string | null;
        ended_at?: string | null;
        answered_at?: string | null;
        status?: string | null;
        is_video?: boolean | null;
        [k: string]: unknown;
      };
      const elefaAsCallRecords: CallRecord[] = (elefaCalls || []).map((ec: RawElefaCall) => {
        const isCaller = ec.caller_id === userId;
        const otherParty = isCaller ? ec.callee : ec.caller;
        const otherPartyId = isCaller ? ec.callee_id : ec.caller_id;
        const otherName = otherParty?.full_name || otherParty?.phone_number || 'elefa User';
        return {
          id: ec.id,
          destination_number: otherName,
          destination_country: 'elefa',
          destination_country_code: '',
          destination_type: 'elefa',
          duration_seconds: ec.duration_seconds,
          rate_per_minute_cents: 0,
          cost_cents: 0,
          status: ec.status === 'ended' ? 'completed' : ec.status,
          started_at: ec.started_at,
          connected_at: ec.answered_at,
          ended_at: ec.ended_at,
          call_quality_score: null,
          is_video: ec.is_video || false,
          elefa_user_id: otherPartyId,
        };
      });

      // Merge and sort by started_at; derive hasMore from the +1 probe row.
      const merged = [...pstnCalls, ...elefaAsCallRecords]
        .sort((a, b) => new Date(b.started_at).getTime() - new Date(a.started_at).getTime());
      const hasMore = merged.length > pageSize;
      const allCalls = merged.slice(0, pageSize);

      return {
        calls: allCalls,
        totalCount: allCalls.length, // exact count not needed for UI
        hasMore,
      };
    },
    enabled: !!userId,
    staleTime: 60_000,
    gcTime: 5 * 60 * 1000,
    // Show last fetched data instantly while refetching - eliminates the
    // "first call shows, then hang" flash on tab switch / app resume.
    placeholderData: (prev) => {
      if (prev) return prev;
      try {
        const cached = localStorage.getItem('elefa-call-history');
        if (cached) return JSON.parse(cached);
      } catch { /* ignore */ }
      return undefined;
    },
  });

  // Persist to localStorage whenever data changes
  if (data) {
    try {
      localStorage.setItem('elefa-call-history', JSON.stringify(data));
    } catch { /* quota exceeded, ignore */ }
  }

  // ─── Real-time call history sync ───────────────────────────────────────
  useEffect(() => {
    if (!userId) return;

    const invalidateHistory = () => {
      try { localStorage.removeItem('elefa-call-history'); } catch { /* ignore */ }
      queryClient.invalidateQueries({ queryKey: ['call-history'] });
      queryClient.invalidateQueries({ queryKey: ['recent-calls'] });
      queryClient.invalidateQueries({ queryKey: ['recent-calls-for-contacts'] });
    };

    // iOS/Android foreground/background cycles can leave the Realtime client with
    // an already-subscribed channel object. Recents must never crash the native app,
    // so native uses lightweight polling; web keeps Realtime for instant updates.
    if (Capacitor.isNativePlatform()) {
      const timer = window.setInterval(invalidateHistory, 15_000);
      return () => window.clearInterval(timer);
    }

    // Unique per-user channel name avoids "callbacks added after subscribe"
    // when React StrictMode / HMR re-mounts before previous channel is GC'd.
    const channelName = `call-history-realtime-${userId}-${Math.random().toString(36).slice(2, 8)}`;
    try {
      const channel = supabase
        .channel(channelName)
        .on('postgres_changes', { event: '*', schema: 'public', table: 'call_history', filter: `user_id=eq.${userId}` }, invalidateHistory)
        .on('postgres_changes', { event: '*', schema: 'public', table: 'elefa_calls', filter: `caller_id=eq.${userId}` }, invalidateHistory)
        .on('postgres_changes', { event: '*', schema: 'public', table: 'elefa_calls', filter: `callee_id=eq.${userId}` }, invalidateHistory)
        .subscribe();

      return () => { void supabase.removeChannel(channel); };
    } catch (err) {
      console.warn('[CallHistory] Realtime unavailable; falling back to polling:', err);
      const timer = window.setInterval(invalidateHistory, 15_000);
      return () => window.clearInterval(timer);
    }
  }, [userId, queryClient]);

  const clearHistoryMutation = useMutation({
    mutationFn: async (range: ClearHistoryRange): Promise<number> => {
      if (!userId) throw new Error('Not authenticated');

      // Soft-delete PSTN rows; per-user hide for shared elefa rows.
      let pstnSelect = supabase
        .from('call_history')
        .select('id')
        .eq('user_id', userId)
        .is('deleted_at', null);

      let elefaSelect = supabase
        .from('elefa_calls')
        .select('id')
        .or(`caller_id.eq.${userId},callee_id.eq.${userId}`)
        .is('deleted_at', null);

      if (range === '7days') {
        const cutoff = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString();
        pstnSelect = pstnSelect.gte('started_at', cutoff);
        elefaSelect = elefaSelect.gte('started_at', cutoff);
      } else if (range === '30days') {
        const cutoff = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString();
        pstnSelect = pstnSelect.gte('started_at', cutoff);
        elefaSelect = elefaSelect.gte('started_at', cutoff);
      }

      const [pstnSelectResult, elefaSelResult] = await Promise.all([
        pstnSelect.limit(2000),
        elefaSelect.limit(2000),
      ]);

      if (pstnSelectResult.error) {
        console.error('Clear PSTN history lookup error:', pstnSelectResult.error);
        throw pstnSelectResult.error;
      }

      const pstnIds = (pstnSelectResult.data ?? []).map((r) => r.id);
      if (pstnIds.length > 0) {
        // 2026-07-19: genuine hard delete. ASTPP cdrs is the server-only
        // append-only record; call_history is the user-facing view and the
        // user's deletion request must be honoured.
        const pstnResult = await supabase
          .from('call_history')
          .delete()
          .in('id', pstnIds)
          .eq('user_id', userId);
        if (pstnResult.error) {
          console.error('Clear PSTN history delete error:', pstnResult.error);
          throw pstnResult.error;
        }
      }

      const elefaIds = (elefaSelResult.data ?? []).map((r) => r.id);
      let elefaHidden = 0;
      if (elefaIds.length > 0) {
        const hideRows = elefaIds.map((call_id) => ({ user_id: userId, call_id }));
        const hideRes = await (supabase.from('elefa_call_hidden' as never) as any)
          .upsert(hideRows, { onConflict: 'user_id,call_id' });
        if (hideRes.error) {
          console.error('Clear elefa hide error:', hideRes.error);
        } else {
          elefaHidden = elefaIds.length;
        }
      }

      return pstnIds.length + elefaHidden;
    },

    onSuccess: (clearedCount, range) => {
      // Invalidate ALL call-related queries so every surface updates
      queryClient.invalidateQueries({ queryKey: ['call-history'] });
      queryClient.invalidateQueries({ queryKey: ['recent-calls-for-contacts'] });
      queryClient.invalidateQueries({ queryKey: ['recent-calls'] });
      
      if (clearedCount === 0) {
        toast.info('No calls to clear in the selected range');
      } else {
        const rangeText = range === 'all' ? 'all' : range === '7days' ? 'last 7 days of' : 'last 30 days of';
        toast.success(`Cleared ${clearedCount} ${clearedCount === 1 ? 'call' : 'calls'} from ${rangeText} history`);
      }
      
      // Force refetch to ensure UI updates immediately
      refetch();
    },
    onError: (error) => {
      console.error('Failed to clear history:', error);
      toast.error('Failed to clear call history');
    },
  });

  const deleteCallMutation = useMutation({
    // Persist PSTN dismissal with deleted_at so background CDR/billing sync can
    // keep updating the retained billing row without making it visible again.
    // For shared elefa<->elefa rows, INSERT a per-user dismissal into
    // elefa_call_hidden so each side can clear their own list independently.
    mutationFn: async (callId: string): Promise<{ deleted: number; table: 'pstn' | 'elefa' | 'none' }> => {
      if (!userId) throw new Error('Not authenticated');

      // Check PSTN ownership before the update; RETURNING can be hidden after
      // deleted_at changes if read policies exclude deleted rows.
      const pstnMatch = await supabase
        .from('call_history')
        .select('id')
        .eq('id', callId)
        .eq('user_id', userId)
        .is('deleted_at', null)
        .maybeSingle();

      if (pstnMatch.error) {
        console.error('[deleteCall] pstn lookup error', pstnMatch.error);
        throw pstnMatch.error;
      }

      if (pstnMatch.data) {
        const pstnDel = await supabase
          .from('call_history')
          .delete()
          .eq('id', callId)
          .eq('user_id', userId);

        if (pstnDel.error) {
          console.error('[deleteCall] pstn delete error', pstnDel.error);
          throw pstnDel.error;
        }
        return { deleted: 1, table: 'pstn' };
      }

      // Not a visible PSTN row; hide on the elefa side for this user.
      const hideRes = await (supabase.from('elefa_call_hidden' as never) as any)
        .upsert({ user_id: userId, call_id: callId }, { onConflict: 'user_id,call_id' });

      const elefaErrorIsBenign = !hideRes.error || hideRes.error.code === '23503';
      if (!elefaErrorIsBenign) {
        console.error('[deleteCall] elefa hide error', hideRes.error);
        throw hideRes.error;
      }
      return { deleted: 1, table: hideRes.error ? 'none' : 'elefa' };
    },


    onMutate: async (callId: string) => {
      // Cancel outgoing refetches so they don't overwrite our optimistic state
      await queryClient.cancelQueries({ queryKey: ['call-history'] });
      await queryClient.cancelQueries({ queryKey: ['recent-calls'] });
      await queryClient.cancelQueries({ queryKey: ['recent-calls-for-contacts'] });

      // Snapshot previous value (covers the *exact* key this hook reads)
      const previous = queryClient.getQueryData<PaginatedCallHistory>(['call-history', userId, page, pageSize]);

      // Optimistically remove the call from cache
      queryClient.setQueryData<PaginatedCallHistory>(
        ['call-history', userId, page, pageSize],
        (old) => {
          if (!old) return old;
          const filtered = old.calls.filter(c => c.id !== callId);
          return { ...old, calls: filtered, totalCount: Math.max(0, old.totalCount - (old.calls.length - filtered.length)) };
        }
      );

      // Drop persisted cache so a cold reload doesn't show the deleted row
      try { localStorage.removeItem('elefa-call-history'); } catch { /* ignore */ }

      return { previous };
    },
    onError: (error, _callId, context) => {
      console.error('Failed to delete call:', error);
      toast.error('Failed to delete call');
      // Rollback on error
      if (context?.previous) {
        queryClient.setQueryData(['call-history', userId, page, pageSize], context.previous);
      }
    },
    onSettled: () => {
      // Confirmed-write invalidate. Because the mutationFn already verified
      // the row was soft-deleted in the DB, the refetch will exclude it
      // (RLS view requires deleted_at IS NULL).
      queryClient.invalidateQueries({ queryKey: ['call-history'] });
      queryClient.invalidateQueries({ queryKey: ['recent-calls'] });
      queryClient.invalidateQueries({ queryKey: ['recent-calls-for-contacts'] });
    },
  });

  const deleteCallsMutation = useMutation({
    // Soft-delete PSTN rows the user owns, and INSERT per-user hides for any
    // ids that belong to the shared elefa_calls table. Because we don't know
    // which table each id belongs to up-front, we just try both - the FK on
    // elefa_call_hidden will harmlessly reject PSTN ids and the PSTN update
    // will harmlessly match zero rows for elefa ids.
    mutationFn: async (callIds: string[]): Promise<number> => {
      if (!userId) throw new Error('Not authenticated');
      if (callIds.length === 0) return 0;

      const pstnLookup = await supabase
        .from('call_history')
        .select('id')
        .in('id', callIds)
        .eq('user_id', userId)
        .is('deleted_at', null);

      if (pstnLookup.error) {
        console.error('[deleteCalls] pstn lookup error', pstnLookup.error);
        throw pstnLookup.error;
      }

      const pstnIds = (pstnLookup.data ?? []).map((row) => row.id);
      const pstnIdSet = new Set(pstnIds);
      const hideRows = callIds
        .filter((call_id) => !pstnIdSet.has(call_id))
        .map((call_id) => ({ user_id: userId, call_id }));
      const pstnUpdate = pstnIds.length > 0
        ? supabase
            .from('call_history')
            .delete()
            .in('id', pstnIds)
            .eq('user_id', userId)
        : Promise.resolve({ error: null });

      const [pstnResult, elefaHideResult] = await Promise.all([
        pstnUpdate,
        hideRows.length > 0
          ? (supabase.from('elefa_call_hidden' as never) as any)
              .upsert(hideRows, { onConflict: 'user_id,call_id' })
              .select('call_id')
          : Promise.resolve({ data: [], error: null }),
      ]);

      const pstnDeleted = pstnIds.length;
      const elefaHidden = elefaHideResult.data?.length ?? 0;

      // FK violation 23503 means an id was already gone from the shared-call
      // table; visible PSTN ids were filtered out before this upsert.
      const elefaErrorIsBenign =
        !elefaHideResult.error || elefaHideResult.error.code === '23503';

      if (pstnResult.error) {
        console.error('[deleteCalls] pstn delete error', pstnResult.error);
        throw pstnResult.error;
      }
      if (!elefaErrorIsBenign) {
        console.error('[deleteCalls] elefa hide error', elefaHideResult.error);
        throw elefaHideResult.error;
      }

      const total = pstnDeleted + elefaHidden;
      if (total === 0) {
        // Nothing matched on either side - treat as already-hidden success.
        return callIds.length;
      }
      return total;
    },



    onMutate: async (callIds: string[]) => {
      await queryClient.cancelQueries({ queryKey: ['call-history'] });
      await queryClient.cancelQueries({ queryKey: ['recent-calls'] });
      await queryClient.cancelQueries({ queryKey: ['recent-calls-for-contacts'] });


      const previous = queryClient.getQueryData<PaginatedCallHistory>(['call-history', userId, page, pageSize]);

      const idSet = new Set(callIds);
      queryClient.setQueryData<PaginatedCallHistory>(
        ['call-history', userId, page, pageSize],
        (old) => {
          if (!old) return old;
          const filtered = old.calls.filter(c => !idSet.has(c.id));
          return { ...old, calls: filtered, totalCount: old.totalCount - (old.calls.length - filtered.length) };
        }
      );

      try { localStorage.removeItem('elefa-call-history'); } catch { /* ignore */ }

      return { previous };
    },
    onSuccess: (count) => {
      if (count > 0) {
        toast.success(`Deleted ${count} ${count === 1 ? 'call' : 'calls'}`);
      }
    },
    onError: (error, _callIds, context) => {
      console.error('Failed to delete calls:', error);
      toast.error('Failed to delete calls');
      if (context?.previous) {
        queryClient.setQueryData(['call-history', userId, page, pageSize], context.previous);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['call-history'] });
      queryClient.invalidateQueries({ queryKey: ['recent-calls'] });
      queryClient.invalidateQueries({ queryKey: ['recent-calls-for-contacts'] });
    },
  });

  const formatDuration = (seconds: number | null): string => {
    if (!seconds) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatCost = (cents: number | null): string => {
    if (cents === null) return '-';
    return `$${(cents / 100).toFixed(2)}`;
  };

  const clearHistory = (range: ClearHistoryRange) => {
    clearHistoryMutation.mutate(range);
  };

  const deleteCall = (callId: string) => {
    deleteCallMutation.mutate(callId);
  };

  const deleteCalls = (callIds: string[]) => {
    deleteCallsMutation.mutate(callIds);
  };

  return {
    calls: data?.calls || [],
    totalCount: data?.totalCount || 0,
    hasMore: data?.hasMore || false,
    isLoading,
    error,
    refetch,
    formatDuration,
    formatCost,
    clearHistory,
    isClearing: clearHistoryMutation.isPending,
    deleteCall,
    deleteCalls,
    isDeletingCall: deleteCallMutation.isPending,
    isDeletingCalls: deleteCallsMutation.isPending,
  };
}
